import { Component, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { LoanValidation } from '../loan-validation/loan-validation';

interface PageAnnotation {
  documentType: string;
  key: string;
  date: string;
  signature: string;
  templateName: string;
  firstPage: boolean;
  lastPage: boolean;
}

interface PdfFile {
  id: string;
  name: string;
  file: File;
  data: Uint8Array;
  uploadDate: Date;
}

interface DocumentType {
  id: string;
  name: string;
  key: string;
  pdfs: PdfFile[];
  expanded: boolean;
}

interface Document {
  id: string;
  name: string;
  documentTypes: DocumentType[];
  expanded: boolean;
}

@Component({
  selector: 'app-document-manager',
  imports: [CommonModule, FormsModule, LoanValidation, PdfViewerModule],
  templateUrl: './document-manager.component.html',
  styleUrls: ['./document-manager.component.css']
})
export class DocumentManagerComponent {
  constructor(private router: Router) { }
  
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('modalFileInput') modalFileInput!: ElementRef<HTMLInputElement>;

  activeTab: string = 'sequencing';
  evalTab: string = 'extraction'; // 'extraction' or 'versioning'

  documents: Document[] = [];
  selectedDocument: Document | null = null;
  selectedDocumentType: DocumentType | null = null;
  selectedPdf: PdfFile | null = null;

  pdfSrc: Uint8Array | null = null;
  totalPages: number = 0;
  currentPage: number = 1;
  zoom: number = 1.0;

  annotation: PageAnnotation = {
    documentType: '',
    key: '',
    date: '',
    signature: '',
    templateName: '',
    firstPage: false,
    lastPage: false
  };

  documentTypes: string[] = ['Invoice', 'Contract', 'Report', 'Receipt', 'Agreement', 'Other'];
  keyOptions: string[] = ['date_issued', 'signature'];
  templateNames: string[] = [];
  savedAnnotations: PageAnnotation[] = [];

  showModal: boolean = false;
  newDocument = {
    documentName: '',
    documentType: '',
    key: ''
  };

  showAddDocumentModal(): void {
    this.showModal = true;
    this.newDocument = {
      documentName: '',
      documentType: '',
      key: ''
    };
  }

  closeModal(): void {
    this.showModal = false;
  }

  saveAndUpload(): void {
    if (this.newDocument.documentName && this.newDocument.documentType && this.newDocument.key) {
      this.modalFileInput.nativeElement.click();
    }
  }

  onModalFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file && file.type === 'application/pdf') {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const arrayBuffer = e.target.result as ArrayBuffer;
        const pdfData = new Uint8Array(arrayBuffer);
        this.addPdfToHierarchy(file, pdfData);
      };
      reader.readAsArrayBuffer(file);
      this.closeModal();
    } else {
      alert('Please select a PDF file');
    }
  }

  addPdfToHierarchy(file: File, pdfData: Uint8Array): void {
    const documentName = this.newDocument.documentName;
    const documentTypeName = this.newDocument.documentType;
    const documentKey = this.newDocument.key;

    let document = this.documents.find(d => d.name === documentName);
    if (!document) {
      document = {
        id: this.generateId(),
        name: documentName,
        documentTypes: [],
        expanded: true
      };
      this.documents.push(document);
    }

    let docType = document.documentTypes.find(dt => dt.name === documentTypeName && dt.key === documentKey);
    if (!docType) {
      docType = {
        id: this.generateId(),
        name: documentTypeName,
        key: documentKey,
        pdfs: [],
        expanded: true
      };
      document.documentTypes.push(docType);
    }

    const pdfFile: PdfFile = {
      id: this.generateId(),
      name: file.name,
      file: file,
      data: pdfData,
      uploadDate: new Date()
    };
    docType.pdfs.push(pdfFile);

    this.selectPdf(document, docType, pdfFile);
  }

  generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  toggleDocument(doc: Document): void {
    doc.expanded = !doc.expanded;
  }

  toggleDocumentType(docType: DocumentType): void {
    docType.expanded = !docType.expanded;
  }

  selectPdf(doc: Document, docType: DocumentType, pdf: PdfFile): void {
    this.selectedDocument = doc;
    this.selectedDocumentType = docType;
    this.selectedPdf = pdf;
    this.pdfSrc = pdf.data;
    this.currentPage = 1;
  }

  afterLoadComplete(pdf: any): void {
    this.totalPages = pdf.numPages;
  }

  onPageChange(event: any): void {
    if (event && typeof event.pageNumber === 'number') {
      this.currentPage = event.pageNumber;
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  zoomIn(): void {
    if (this.zoom < 3.0) {
      this.zoom += 0.25;
    }
  }

  zoomOut(): void {
    if (this.zoom > 0.5) {
      this.zoom -= 0.25;
    }
  }

  onPdfLoadComplete(pdf: any): void {
    this.totalPages = pdf.numPages;
    this.currentPage = 1;
  }

  saveAnnotation(): void {
    if (!this.annotation.documentType || !this.annotation.key) {
      alert('Please fill in Document Type and Key fields');
      return;
    }

    const annotationCopy = { ...this.annotation };
    this.savedAnnotations.push(annotationCopy);
    
    if (this.annotation.templateName && !this.templateNames.includes(this.annotation.templateName)) {
      this.templateNames.push(this.annotation.templateName);
    }
    alert('Annotation saved successfully!');
    
    this.annotation = {
      documentType: '',
      key: '',
      date: '',
      signature: '',
      templateName: '',
      firstPage: false,
      lastPage: false
    };
  }

  clearDocument(): void {
    this.selectedDocument = null;
    this.selectedDocumentType = null;
    this.selectedPdf = null;
    this.pdfSrc = null;
    this.totalPages = 0;
    this.currentPage = 1;
  }

  setActiveTab(tab: string): void {
    this.activeTab = tab;
    if (tab === 'sequencing') {
      this.router.navigate(['/']);
    } else if (tab === 'dataextraction') {
      this.router.navigate(['/data-extraction']);
    }
  }

  navigateToSequencing(): void {
    this.router.navigate(['/']);
  }

  navigateToDataExtraction(): void {
    this.router.navigate(['/data-extraction']);
  }

  navigateToVersioning(): void {
    this.router.navigate(['/versioning']);
  }

  navigateToExternalMapping(): void {
    this.router.navigate(['/dashboard']);
  }

  navigateToEval(): void {
    this.router.navigate(['/dashboard']);
  }

  setEvalTab(tab: string): void {
    this.evalTab = tab;
  }

  openVersioningEval(): void {
    alert('Opening Versioning Eval - This will navigate to versioning evaluation page');
  }

  openExtractionEval(): void {
    this.router.navigate(['/dashboard']);
  }
}
